import React, { useState, useEffect, useMemo, useCallback, memo } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import {
    indexConceptsByTeacherType,
    clssInfo,
    GetMatter,
    GetMatterDetails,
    updateGrade,
    getIstQuarter,
    getIIndQuarter,
    getIIIrdQuarter,
    getIVthQuarter,
    getVthQuarter,
    getVIthQuarter
} from '../../Api'

import {
    Container,
    ContainerDivs,
    Header,
    Title,
    NewActivityButton,
    Toolbar,
    ToolbarActions,
    SearchInput,
    ToggleAllButton,
    EmptyState,
    MatterCard,
    MatterHeader,
    MatterTitle,
    Badge,
    ChevronWrap,
    MatterBody,
    BimesterCard,
    BimesterHeader,
    BimesterTitle,
    BimesterBody,
    ActivityMeta,
    ActivityActions,
    Tag,
    ConceptTag,
    TagSpan,
    EditButton,
    ToGoBack,
    SignMessageButtonText,
    SignMessageButtonTextBold,
    FormCard,
    FormHeader,
    FormTitle,
    FormSubtitle,
    FormGroup,
    FormLabel,
    FormSelect,
    FormError,
    FormActions,
    SaveButton,
    CancelButton,
    Overlay,
    SkeletonCard,
    SkeletonLine
} from './style'

// ── helpers fora do componente (não recriam a cada render) ──────────
const extractId = (v) => {
    if (!v) return null
    if (typeof v === 'string') {
        const t = v.trim()
        if ((t.startsWith('"') && t.endsWith('"')) ||
            (t.startsWith('[') && t.endsWith(']')) ||
            (t.startsWith('{') && t.endsWith('}'))) {
            try { v = JSON.parse(t) } catch { /* mantém v */ }
        }
    }
    if (Array.isArray(v)) v = v[0]
    if (v && typeof v === 'object') v = v._id ?? v.id ?? null
    const s = String(v ?? '').trim()
    return /^[a-fA-F0-9]{24}$/.test(s) ? s : null
}

const safeParse = (raw) => {
    try { return raw ? JSON.parse(raw) : null } catch { return null }
}

const bimOrder = (b) => parseInt(String(b).match(/\d+/)?.[0] || '99', 10)

// nome do aluno a partir do campo (populado ou não)
const studentName = (c) =>
    (c.id_student && typeof c.id_student === 'object' ? c.id_student.name : null)
    || c.studentName
    || 'Aluno'

// rótulo do conceito (trata "-" / vazio como "Sem conceito")
const conceptLabel = (v) =>
    v === '-' || v == null || v === '' ? 'Não Adicionado' : v

/* ── Componente da linha de conceito (memoizado) ─────────────────
   Evita re-render de todas as linhas quando o usuário digita na busca
   ou abre/fecha outro bimestre.
   isConceptOpen segue o mesmo controle da página de atividades:
   o bimestre precisa estar ABERTO para o tipo de professor.        */
const ConceptRowItem = memo(function ConceptRowItem({ concept, isConceptOpen, onEdit }) {
    const nome = studentName(concept)
    const valor = concept.studentGrade

    return (
        <TagSpan>
            <Tag>{nome}</Tag>

            <ActivityActions>
                <ConceptTag grade={valor}>{conceptLabel(valor)}</ConceptTag>

                <EditButton
                    disabled={!isConceptOpen}
                    onClick={() => isConceptOpen && onEdit(concept)}
                    aria-label={`Editar conceito de ${nome}`}
                    title={isConceptOpen ? 'Editar conceito' : 'Período fechado'}
                >
                    {isConceptOpen ? 'Editar' : 'Período Fechado'}
                </EditButton>
            </ActivityActions>
        </TagSpan>
    )
})

const Concepts = () => {
    const navigate = useNavigate()
    const location = useLocation()
    // funcionário (professor) escolhido pelo supervisor na tela anterior
    const { employee } = location.state || {}

    // ── listagem ─────────────────────────────────────────────────────
    const [concepts, setConcepts] = useState([])
    const [loading, setLoading] = useState(true)             // dados principais
    const [loadingSecondary, setLoadingSecondary] = useState(true) // matérias/bimestres
    const [busca, setBusca] = useState('')
    const [debouncedBusca, setDebouncedBusca] = useState('')
    const [openMatters, setOpenMatters] = useState({})
    const [openBimesters, setOpenBimesters] = useState({})

    // ── contexto da turma/professor ──────────────────────────────────
    const [matters, setMatters] = useState([])
    const [year, setYear] = useState('')
    const [bimesterById, setBimesterById] = useState({})
    const [assessmentRegime, setAssessmentRegime] = useState('')
    const [dailyStatus, setDailyStatus] = useState({})       // status de diário por bimestre
    const [isPhysicalTeacher, setIsPhysicalTeacher] = useState(false)

    const periodLabel = assessmentRegime === 'TRIMESTRAL' ? 'Trimestre' : 'Bimestre'
    const periodLabelLower = assessmentRegime === 'TRIMESTRAL' ? 'trimestre' : 'bimestre'

    // ── modal de novo conceito (seleciona bimestre + matéria) ────────
    const [showForm, setShowForm] = useState(false)
    const [submitting, setSubmitting] = useState(false)
    const [errorMessage, setErrorMessage] = useState('')
    const [form, setForm] = useState({ bimonthly: '', id_matter: '' })

    // ── modal de edição de conceito ──────────────────────────────────
    const [editingConcept, setEditingConcept] = useState(null) // conceito sendo editado
    const [editGrade, setEditGrade] = useState('')             // novo valor A/B/C/D/-
    const [savingEdit, setSavingEdit] = useState(false)
    const [editError, setEditError] = useState('')

    // ── debounce da busca ────────────────────────────────────────────
    useEffect(() => {
        const t = setTimeout(() => setDebouncedBusca(busca), 300)
        return () => clearTimeout(t)
    }, [busca])

    // ── carga em DUAS FASES (carregamento progressivo) ───────────────
    // Fase 1: turma + conceitos  → libera a UI o quanto antes
    // Fase 2: matérias + bimestres → carregam em background
    useEffect(() => {
        let cancelled = false

            ; (async () => {
                setLoading(true)
                setLoadingSecondary(true)

                const id_class = sessionStorage.getItem('class-info')
                // supervisor: o funcionário vem do state da navegação (não do localStorage)
                const id_emp = employee
                const idSchool = safeParse(sessionStorage.getItem('id-school'))
                const physicalRaw = sessionStorage.getItem('physicalEducationTeacher')

                const myId = extractId(id_emp)
                const physicalId = extractId(safeParse(physicalRaw))

                const amIPhysical = !!physicalId && myId === physicalId
                const teacher_type = amIPhysical ? 'ED_FISICA' : 'REGENTE'

                try {
                    // ── FASE 1 ─────────────────────────────────────────
                    const resClass = await clssInfo(id_class)
                    if (cancelled) return

                    const turma = resClass?.data?.data?.[0]
                    const yearValue = turma?.year

                    sessionStorage.setItem('yearGrade', yearValue)

                    // dispara conceitos já com o year correto
                    const conceptPromise = indexConceptsByTeacherType({
                        id_class,
                        year: yearValue,
                        teacher_type
                    })

                    // dispara dados secundários em PARALELO (mas sem bloquear)
                    const secondaryPromise = Promise.all([
                        GetMatter(idSchool),
                        getIstQuarter(yearValue, idSchool),
                        getIIndQuarter(yearValue, idSchool),
                        getIIIrdQuarter(yearValue, idSchool),
                        getIVthQuarter(yearValue, idSchool),
                        getVthQuarter(yearValue, idSchool),
                        getVIthQuarter(yearValue, idSchool)
                    ])

                    // espera só pelos conceitos (dado mais importante)
                    const resConcept = await conceptPromise
                    if (cancelled) return

                    const list = resConcept?.data?.data || []

                    // libera a UI principal
                    setConcepts(list)
                    setYear(yearValue)
                    setDailyStatus(turma?.dailyStatus || {}) // status de diário por bimestre
                    setIsPhysicalTeacher(amIPhysical)

                    // todos os bimestres iniciam fechados (recolhidos)

                    setLoading(false)

                    // ── FASE 2 ─────────────────────────────────────────
                    const [resMatter, q1, q2, q3, q4, q5, q6] = await secondaryPromise
                    if (cancelled) return

                    const allMatters = resMatter?.data?.data || []
                    const filteredMatters = allMatters.filter(m =>
                        amIPhysical ? m.name === 'EDUCAÇÃO FÍSICA' : m.name !== 'EDUCAÇÃO FÍSICA'
                    )

                    const pickFirst = (q) => q?.data?.data?.find(r => r) || null
                    const i = pickFirst(q1)
                    const ii = pickFirst(q2)
                    const iii = pickFirst(q3)
                    const iv = pickFirst(q4)
                    const v = pickFirst(q5)
                    const vi = pickFirst(q6)

                    const map = {}
                    if (i) map['1º BIMESTRE'] = i
                    if (ii) map['2º BIMESTRE'] = ii
                    if (iii) map['3º BIMESTRE'] = iii
                    if (iv) map['4º BIMESTRE'] = iv
                    if (v) map['5º BIMESTRE'] = v
                    if (vi) map['6º BIMESTRE'] = vi

                    setMatters(filteredMatters)
                    setBimesterById(map)
                    setAssessmentRegime(i?.assessmentRegime || '')
                    setLoadingSecondary(false)
                } catch (err) {
                    console.error('Erro ao carregar página:', err)
                    if (!cancelled) {
                        setLoading(false)
                        setLoadingSecondary(false)
                    }
                }
            })()

        return () => { cancelled = true }
    }, [employee])

    // ── scroll lock + ESC quando algum modal está aberto ─────────────
    useEffect(() => {
        const anyModal = showForm || !!editingConcept
        if (!anyModal) return

        const prevOverflow = document.body.style.overflow
        document.body.style.overflow = 'hidden'

        const onKey = (e) => {
            if (e.key !== 'Escape') return
            if (!submitting && !savingEdit) {
                setShowForm(false)
                setErrorMessage('')
                setEditingConcept(null)
                setEditError('')
            }
        }
        window.addEventListener('keydown', onKey)

        return () => {
            document.body.style.overflow = prevOverflow
            window.removeEventListener('keydown', onKey)
        }
    }, [showForm, editingConcept, submitting, savingEdit])

    // ── status do diário: o bimestre está aberto para este professor? ─
    // Mesmo controle da página de atividades: dailyStatus[bimonthly] traz
    // regentTeacher / physicalEducationTeacher; aberto quando !== 'fechado'.
    const isBimesterOpenFor = useCallback((bimonthly) => {
        const st = dailyStatus?.[bimonthly]
        if (!st) return true
        const field = isPhysicalTeacher ? st.physicalEducationTeacher : st.regentTeacher
        return field !== 'fechado'
    }, [dailyStatus, isPhysicalTeacher])

    // ── bimestres disponíveis no form ────────────────────────────────
    const availableBimesters = useMemo(() => {
        const base = assessmentRegime === 'TRIMESTRAL'
            ? ['1º BIMESTRE', '2º BIMESTRE', '3º BIMESTRE']
            : ['1º BIMESTRE', '2º BIMESTRE', '3º BIMESTRE', '4º BIMESTRE']
        return base.filter(b => bimesterById[b])
    }, [assessmentRegime, bimesterById])

    // ── agrupamento: bimestre → matéria → conceitos ─────────────────
    const grouped = useMemo(() => {
        const q = debouncedBusca.trim().toLowerCase()
        const groups = {}

        for (const c of concepts) {
            if (q) {
                const haystack = [studentName(c), c.studentGrade, c.bimonthly, c.id_matter?.name]
                    .filter(Boolean).join(' ').toLowerCase()
                if (!haystack.includes(q)) continue
            }

            const bim = c.bimonthly || 'SEM BIMESTRE'
            const matterId = c.id_matter?._id || 'sem_materia'
            const matterName = c.id_matter?.name || 'Sem matéria'

            if (!groups[bim]) {
                groups[bim] = { bimester: bim, matters: {}, total: 0 }
            }
            if (!groups[bim].matters[matterId]) {
                groups[bim].matters[matterId] = {
                    id: matterId,
                    name: matterName,
                    concepts: [],
                    total: 0,
                    counts: { A: 0, B: 0, C: 0, D: 0 }
                }
            }

            const m = groups[bim].matters[matterId]
            m.concepts.push(c)
            m.total++
            if (m.counts[c.studentGrade] !== undefined) m.counts[c.studentGrade]++
            groups[bim].total++
        }

        return Object.values(groups)
            .sort((a, b) => bimOrder(a.bimester) - bimOrder(b.bimester))
            .map(b => ({
                ...b,
                mattersList: Object.values(b.matters)
                    .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'))
                    .map(m => ({
                        ...m,
                        concepts: m.concepts
                            .slice()
                            .sort((a, b) => studentName(a).localeCompare(studentName(b), 'pt-BR'))
                    }))
            }))
    }, [concepts, debouncedBusca])

    const totalConceitos = useMemo(
        () => grouped.reduce((acc, b) => acc + b.total, 0),
        [grouped]
    )

    // ── toggles ──────────────────────────────────────────────────────
    const toggleMatter = useCallback((id) =>
        setOpenMatters(prev => ({ ...prev, [id]: !prev[id] })), [])

    const toggleBimester = useCallback((key) =>
        setOpenBimesters(prev => ({ ...prev, [key]: !prev[key] })), [])

    const allExpanded = useMemo(() => {
        if (grouped.length === 0) return false
        return grouped.every(b => {
            if (!openBimesters[b.bimester]) return false
            return b.mattersList.every(m =>
                openMatters[`${b.bimester}-${m.id}`]
            )
        })
    }, [grouped, openBimesters, openMatters])

    const toggleAll = useCallback(() => {
        if (allExpanded) {
            setOpenBimesters({})
            setOpenMatters({})
        } else {
            const bims = {}, mts = {}
            for (const b of grouped) {
                bims[b.bimester] = true
                for (const m of b.mattersList) {
                    mts[`${b.bimester}-${m.id}`] = true
                }
            }
            setOpenBimesters(bims)
            setOpenMatters(mts)
        }
    }, [allExpanded, grouped])

    // ── form de novo conceito ────────────────────────────────────────
    const openCreate = () => {
        setErrorMessage('')
        setForm({ bimonthly: '', id_matter: '' })
        setShowForm(true)
    }

    const closeForm = () => {
        if (submitting) return
        setShowForm(false)
        setErrorMessage('')
    }

    const handleChange = (e) => {
        const { name, value } = e.target
        setForm(prev => ({ ...prev, [name]: value }))
        setErrorMessage('')
    }

    // Salva contexto no sessionStorage e redireciona para /grade.
    // A página /grade busca os ids dos bimestres sozinha e envia conforme
    // o bimestre selecionado, então aqui só precisamos da matéria + ano.
    const handleSubmit = async (e) => {
        if (e?.preventDefault) e.preventDefault()
        if (submitting) return

        if (!form.bimonthly) {
            setErrorMessage('Selecione um periodo.')
            return
        }
        if (!form.id_matter) {
            setErrorMessage('Selecione uma matéria.')
            return
        }
        // bloqueia criar conceito em bimestre fechado (por tipo de professor)
        if (!isBimesterOpenFor(form.bimonthly)) {
            setErrorMessage('O diário deste periodo está fechado. Contate o Supervisor.')
            return
        }

        // mapa bimestre → { idKey no sessionStorage, rota de conceito }
        // mesmo esquema do signClick antigo, mas só conceito (sem checar assessmentFormat)
        // rotas do supervisor — repassam o employee no state
        const bimesterRoutes = {
            '1º BIMESTRE': { idKey: 'id-I', route: '/Supervisor-grade-istquarter' },
            '2º BIMESTRE': { idKey: 'id-II', route: '/Supervisor-grade-iindquarter' },
            '3º BIMESTRE': { idKey: 'id-III', route: '/Supervisor-grade-iiirdquarter' },
            '4º BIMESTRE': { idKey: 'id-IV', route: '/Supervisor-grade-ivthquarter' },
            '5º BIMESTRE': { idKey: 'id-V', route: '/grade-vthquarter' },
            '6º BIMESTRE': { idKey: 'id-VI', route: '/grade-vithquarter' }
        }

        const target = bimesterRoutes[form.bimonthly]
        const quarter = bimesterById[form.bimonthly] // objeto do quarter (tem _id)

        if (!target || !quarter?._id) {
            setErrorMessage('Erro, Verifique os dados e tente novamente.')
            return
        }

        setErrorMessage('')
        setSubmitting(true)

        try {
            // pega o nome da matéria (mesmo padrão do signClick/handleDefineMatter)
            const Matter = await GetMatterDetails(form.id_matter)
            if (Matter) {
                sessionStorage.setItem('nameMatter', Matter.data.name)
            }

            sessionStorage.setItem('Selectmatter', form.id_matter)
            sessionStorage.setItem('yearGrade', year)

            // salva o id do bimestre (id-I, id-II, ...) e navega para a rota de conceito
            sessionStorage.setItem(target.idKey, quarter._id)

            closeForm()
            navigate(target.route, { state: { employee } })
        } catch (err) {
            console.error(err)
            setErrorMessage('Erro ao abrir o cadastro de conceito. Tente novamente.')
            setSubmitting(false)
        }
    }

    // ── edição inline de conceito ────────────────────────────────────
    // Só abre se o bimestre estiver aberto para o tipo de professor.
    const openEdit = useCallback((concept) => {
        if (!isBimesterOpenFor(concept.bimonthly)) return
        setEditError('')
        setEditingConcept(concept)
        setEditGrade(concept.studentGrade ?? '')
    }, [isBimesterOpenFor])

    const closeEdit = () => {
        if (savingEdit) return
        setEditingConcept(null)
        setEditError('')
    }

    // Salva via updateGrade(id, studentGrade) — mesma chamada da página
    // de conceito por bimestre. Atualiza a lista em memória (sem reload).
    const saveEdit = async () => {
        if (savingEdit || !editingConcept) return

        // trava de segurança: bimestre precisa continuar aberto
        if (!isBimesterOpenFor(editingConcept.bimonthly)) {
            setEditError('O diário deste periodo está fechado. Contate o Supervisor.')
            return
        }
        if (!editGrade) {
            setEditError('Selecione um conceito ou marque "Não adicionar nota".')
            return
        }

        setEditError('')
        setSavingEdit(true)

        try {
            const res = await updateGrade(editingConcept._id, editGrade)
            const ok = res && (res.status === 200 || res.data?.success || res.data)

            if (ok) {
                setConcepts(prev => prev.map(c =>
                    c._id === editingConcept._id
                        ? { ...c, studentGrade: editGrade }
                        : c
                ))
                setEditingConcept(null)
            } else {
                setEditError('Não foi possível salvar. Tente novamente.')
            }
        } catch (err) {
            console.error('Erro ao atualizar conceito:', err)
            setEditError('Erro ao salvar o conceito. Tente novamente.')
        } finally {
            setSavingEdit(false)
        }
    }

    // ── render ───────────────────────────────────────────────────────
    // Skeleton enquanto carrega o essencial (conceitos)
    if (loading) {
        return (
            <Container>
                <ContainerDivs>
                    <Header>
                        <Title>Meus Conceitos</Title>
                    </Header>
                    {[1, 2, 3].map(i => (
                        <SkeletonCard key={i}>
                            <SkeletonLine $w="40%" $h="18px" />
                            <SkeletonLine $w="80%" />
                            <SkeletonLine $w="60%" />
                        </SkeletonCard>
                    ))}
                </ContainerDivs>
            </Container>
        )
    }

    const editStudentName = editingConcept ? studentName(editingConcept) : ''

    return (
        <Container>
            <ContainerDivs>
                <Header>
                    <Title>Meus Conceitos</Title>
                    <NewActivityButton
                        onClick={openCreate}
                        disabled={loadingSecondary}
                        title={loadingSecondary ? 'Carregando dados...' : 'Adicionar novo conceito'}
                    >
                        + Novo Conceito
                    </NewActivityButton>
                </Header>

                <Toolbar>
                    <SearchInput
                        type="text"
                        placeholder={`Buscar por aluno, conceito, ${periodLabelLower} ou matéria...`}
                        value={busca}
                        onChange={(e) => setBusca(e.target.value)}
                        aria-label="Buscar conceitos"
                    />
                    <ToolbarActions>
                        <Badge>
                            {totalConceitos} {totalConceitos === 1 ? 'conceito' : 'conceitos'}
                        </Badge>
                        {grouped.length > 0 && (
                            <ToggleAllButton onClick={toggleAll}>
                                {allExpanded ? 'Colapsar tudo' : 'Expandir tudo'}
                            </ToggleAllButton>
                        )}
                    </ToolbarActions>
                </Toolbar>

                {grouped.length === 0 ? (
                    <EmptyState>
                        {debouncedBusca
                            ? `Nenhum conceito encontrado para "${debouncedBusca}".`
                            : 'Nenhum conceito encontrado.'}
                    </EmptyState>
                ) : (
                    grouped.map((bim) => {
                        const bimOpen = !!openBimesters[bim.bimester]
                        // bimestre aberto para edição (por tipo de professor)
                        const canEditBim = isBimesterOpenFor(bim.bimester)

                        return (
                            <BimesterCard key={bim.bimester}>
                                <BimesterHeader
                                    $open={bimOpen}
                                    onClick={() => toggleBimester(bim.bimester)}
                                    aria-expanded={bimOpen}
                                >
                                    <BimesterTitle>
                                        <span>
                                            Conceitos do {bim.bimester.replace('BIMESTRE', periodLabel.toUpperCase())}
                                        </span>
                                        <Badge>Total: {bim.mattersList.length}</Badge>
                                    </BimesterTitle>
                                    <ActivityMeta>
                                        <ChevronWrap $open={bimOpen}>▼</ChevronWrap>
                                    </ActivityMeta>
                                </BimesterHeader>

                                {bimOpen && (
                                    <BimesterBody>
                                        {bim.mattersList.map((m) => {
                                            const matterKey = `${bim.bimester}-${m.id}`
                                            const matterOpen = !!openMatters[matterKey]

                                            return (
                                                <MatterCard key={matterKey}>
                                                    <MatterHeader
                                                        $open={matterOpen}
                                                        onClick={() => toggleMatter(matterKey)}
                                                        aria-expanded={matterOpen}
                                                    >
                                                        <MatterTitle>
                                                            {m.name}
                                                            <Badge>{m.total}</Badge>
                                                        </MatterTitle>
                                                        <ActivityMeta>
                                                            <Tag $variant="tipo">A: {m.counts.A}</Tag>
                                                            <Tag $variant="media">B: {m.counts.B}</Tag>
                                                            <Tag $variant="valor">C: {m.counts.C}</Tag>
                                                            <Tag $variant="c">D: {m.counts.D}</Tag>
                                                            <ChevronWrap $open={matterOpen}>▼</ChevronWrap>
                                                        </ActivityMeta>
                                                    </MatterHeader>

                                                    {matterOpen && (
                                                        <MatterBody>
                                                            {m.concepts.map((c) => (
                                                                <ConceptRowItem
                                                                    key={c._id}
                                                                    concept={c}
                                                                    isConceptOpen={canEditBim}
                                                                    onEdit={openEdit}
                                                                />
                                                            ))}
                                                        </MatterBody>
                                                    )}
                                                </MatterCard>
                                            )
                                        })}
                                    </BimesterBody>
                                )}
                            </BimesterCard>
                        )
                    })
                )}

                <ToGoBack onClick={() => navigate(-1)} role="button" tabIndex={0}>
                    <SignMessageButtonText>Voltar para a</SignMessageButtonText>
                    <SignMessageButtonTextBold>Turma</SignMessageButtonTextBold>
                </ToGoBack>
            </ContainerDivs>

            {/* MODAL DE NOVO CONCEITO (seleciona bimestre + matéria → /grade) */}
            {showForm && (
                <Overlay onClick={closeForm}>
                    <FormCard onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
                        <FormHeader>
                            <FormTitle>Adicionar Conceito</FormTitle>
                            <FormSubtitle>
                                Selecione o {periodLabelLower} e a matéria para lançar os conceitos
                            </FormSubtitle>
                        </FormHeader>

                        <FormGroup>
                            <FormLabel>{periodLabel}</FormLabel>
                            <FormSelect
                                name="bimonthly"
                                value={form.bimonthly}
                                onChange={handleChange}
                            >
                                <option value="">Selecione</option>
                                {availableBimesters.length === 0 ? (
                                    <option disabled value="">
                                        Sem {periodLabelLower} configurados
                                    </option>
                                ) : (
                                    availableBimesters.map(b => {
                                        const fechado = !isBimesterOpenFor(b)
                                        return (
                                            <option key={b} value={b}>
                                                {b.replace('BIMESTRE', periodLabel.toUpperCase())}
                                                {fechado ? ' · FECHADO' : ''}
                                            </option>
                                        )
                                    })
                                )}
                            </FormSelect>
                        </FormGroup>

                        <FormGroup>
                            <FormLabel>Matéria</FormLabel>
                            <FormSelect
                                name="id_matter"
                                value={form.id_matter}
                                onChange={handleChange}
                            >
                                <option value="">Selecione</option>
                                {matters.map(m => (
                                    <option key={m._id} value={m._id}>{m.name}</option>
                                ))}
                            </FormSelect>
                        </FormGroup>

                        {form.bimonthly && !isBimesterOpenFor(form.bimonthly) && (
                            <FormError>
                                O diário deste periodo está fechado. Não é possível adicionar conceito.
                            </FormError>
                        )}

                        {errorMessage && <FormError>{errorMessage}</FormError>}

                        <FormActions>
                            <CancelButton onClick={closeForm} disabled={submitting}>
                                Cancelar
                            </CancelButton>
                            <SaveButton
                                onClick={handleSubmit}
                                disabled={submitting || (form.bimonthly && !isBimesterOpenFor(form.bimonthly))}
                            >
                                {submitting ? 'Abrindo...' : 'Continuar'}
                            </SaveButton>
                        </FormActions>
                    </FormCard>
                </Overlay>
            )}

            {/* MODAL DE EDIÇÃO DE CONCEITO (inline via updateGrade) */}
            {editingConcept && (
                <Overlay onClick={closeEdit}>
                    <FormCard onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
                        <FormHeader>
                            <FormTitle>Editar Conceito</FormTitle>
                            <FormSubtitle>
                                {editStudentName}
                                {editingConcept.bimonthly ? ` · ${editingConcept.bimonthly.replace('BIMESTRE', periodLabel.toUpperCase())}` : ''}
                            </FormSubtitle>
                        </FormHeader>

                        <FormGroup>
                            <FormLabel>Conceito</FormLabel>
                            <FormSelect
                                name="editGrade"
                                value={editGrade}
                                onChange={(e) => { setEditGrade(e.target.value); setEditError('') }}
                                disabled={editGrade === '-'}
                            >
                                <option value="">Selecione</option>
                                <option value="A">A</option>
                                <option value="B">B</option>
                                <option value="C">C</option>
                                <option value="D">D</option>
                            </FormSelect>
                        </FormGroup>

                        <FormGroup>
                            <FormLabel>
                                <input
                                    type="checkbox"
                                    checked={editGrade === '-'}
                                    onChange={(e) => {
                                        setEditGrade(e.target.checked ? '-' : '')
                                        setEditError('')
                                    }}
                                    style={{ marginRight: '6px' }}
                                />
                                Não adicionar nota para este aluno
                            </FormLabel>
                        </FormGroup>

                        {editError && <FormError>{editError}</FormError>}

                        <FormActions>
                            <CancelButton onClick={closeEdit} disabled={savingEdit}>
                                Cancelar
                            </CancelButton>
                            <SaveButton onClick={saveEdit} disabled={savingEdit}>
                                {savingEdit ? 'Atualizando...' : 'Atualizar'}
                            </SaveButton>
                        </FormActions>
                    </FormCard>
                </Overlay>
            )}
        </Container>
    )
}

export default Concepts
